import { Fragment, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import Pagination from './Pagination'

const buildEmptyForm = (fields) =>
    Object.fromEntries(fields.map((field) => [field.name, '']))

const stackedFallback = (columns, row) => (
    <>
        {columns.map((col) => (
            <div key={col.header} className="small">
                <span className="text-muted me-1">{col.header}:</span>
                {col.render(row)}
            </div>
        ))}
    </>
)

const ReconciliationTab = ({
    queryKey,
    queryFn,
    mutationFn,
    idAccessor,
    columns,
    fields,
    noun,
    modalTitle,
    successMessage,
    emptyMessage,
    describeRow,
    seedForm,
    mobileCard,
}) => {
    const queryClient = useQueryClient()

    const [page, setPage]         = useState(1)
    const [selected, setSelected] = useState(null)
    const [form, setForm]         = useState(() => buildEmptyForm(fields))

    const { status, data, error } = useQuery({
        queryKey: [queryKey, page],
        queryFn:  () => queryFn(page),
        placeholderData: keepPreviousData,
        staleTime: 0,
    })

    const rows     = data?.data?.data ?? []
    const lastPage = data?.data?.meta?.last_page ?? 1

    const reconcileMutation = useMutation({
        mutationFn: ({ id, payload }) => mutationFn(id, payload),
        onSuccess: async () => {
            toast.success(successMessage)
            await queryClient.invalidateQueries({ queryKey: [queryKey] })
            handleClose()
        },
        onError: (err) => {
            const message = err?.response?.data?.message || `Failed to reconcile ${noun}. Please try again.`
            toast.error(message)
        },
    })

    const openModal = (row) => {
        setSelected(row)
        const base = buildEmptyForm(fields)
        setForm(seedForm ? seedForm(row, base) : base)
    }

    const handleClose = () => {
        setSelected(null)
        setForm(buildEmptyForm(fields))
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        reconcileMutation.mutate({
            id:      idAccessor(selected),
            payload: fields.reduce((payload, field) => {
                payload[field.name] = field.type === 'number' ? parseFloat(form[field.name]) : form[field.name]
                return payload
            }, {}),
        })
    }

    const allFieldsFilled = fields.every((field) => form[field.name] !== '' && form[field.name] !== null && form[field.name] !== undefined)
    const desktopColSpan  = columns.length + 1
    const formId          = `reconcile-${noun.replace(/\s+/g, '-')}-form`

    const summary = (row) => (mobileCard ? mobileCard(row) : stackedFallback(columns, row))

    const reconcileButton = (row) => (
        <button
            type="button"
            className="btn btn-sm btn-warning"
            onClick={() => openModal(row)}
        >
            Reconcile
        </button>
    )

    const statusRow = (colSpan, content, className = '') => (
        <tr>
            <td colSpan={colSpan} className={`text-center py-4 ${className}`.trim()}>
                {content}
            </td>
        </tr>
    )

    const loadingContent  = <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading</span></div>
    const errorContent    = <>Failed to load {noun} list. Please try again.</>

    const isPending = status === 'pending'
    const isEmpty   = !isPending && !error && rows.length === 0

    return (
        <Fragment>
            <div className="table-responsive d-none d-xl-block px-3 pt-2">
                <table className="table verticle-middle mb-0">
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th scope="col" key={col.header}>{col.header}</th>
                            ))}
                            <th scope="col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {isPending
                            ? statusRow(desktopColSpan, loadingContent)
                            : error
                                ? statusRow(desktopColSpan, errorContent, 'text-danger')
                                : isEmpty
                                    ? statusRow(desktopColSpan, emptyMessage)
                                    : rows.map((row) => (
                                        <tr key={idAccessor(row)}>
                                            {columns.map((col) => (
                                                <td key={col.header} className={col.className || ''}>{col.render(row)}</td>
                                            ))}
                                            <td className="text-nowrap">{reconcileButton(row)}</td>
                                        </tr>
                                    ))
                        }
                    </tbody>
                </table>
            </div>

            <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                <table className="table verticle-middle mb-0">
                    <thead>
                        <tr>
                            <th scope="col">Summary</th>
                            <th scope="col" className="text-end">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isPending
                            ? statusRow(2, loadingContent)
                            : error
                                ? statusRow(2, errorContent, 'text-danger')
                                : isEmpty
                                    ? statusRow(2, emptyMessage)
                                    : rows.map((row) => (
                                        <tr key={idAccessor(row)} className="align-top">
                                            <td>{summary(row)}</td>
                                            <td className="text-end text-nowrap">{reconcileButton(row)}</td>
                                        </tr>
                                    ))
                        }
                    </tbody>
                </table>
            </div>

            <div className="d-block d-md-none">
                {isPending && (
                    <div className="d-flex justify-content-center py-4">
                        <span className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                )}
                {error && (
                    <p className="text-center text-danger py-4 mb-0">Failed to load {noun} list. Please try again.</p>
                )}
                {isEmpty && (
                    <p className="text-center text-muted py-4 mb-0 px-3">{emptyMessage}</p>
                )}
                {!isPending && !error && !isEmpty && (
                    <ul className="list-group list-group-flush">
                        {rows.map((row) => (
                            <li key={idAccessor(row)} className="list-group-item py-3">
                                {summary(row)}
                                <div className="mt-2">{reconcileButton(row)}</div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <Pagination page={page} totalPages={lastPage} onPageChange={setPage} />

            <Modal show={!!selected} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selected && describeRow(selected)}
                    <form id={formId} onSubmit={handleSubmit}>
                        {fields.map((field) => (
                            <div className="mb-3" key={field.name}>
                                <label className="form-label">
                                    {field.label} <span className="text-danger">*</span>
                                </label>
                                <input
                                    type={field.type}
                                    className="form-control"
                                    placeholder={field.placeholder}
                                    maxLength={field.maxLength}
                                    pattern={field.pattern}
                                    min={field.min}
                                    step={field.step}
                                    value={form[field.name]}
                                    onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                                    required
                                />
                                {field.help && <div className="form-text">{field.help}</div>}
                            </div>
                        ))}
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={reconcileMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form={formId}
                        variant="warning"
                        disabled={reconcileMutation.isPending || !allFieldsFilled}
                    >
                        {reconcileMutation.isPending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Reconciling
                            </>
                        ) : (
                            'Confirm Reconciliation'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    )
}

export default ReconciliationTab
